import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Field } from 'components/Wizards/Field/Field'
import { Formik } from 'formik'
import Styles from './WordPressInitialization.module.scss'
import * as yup from 'yup'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Text } from 'almanac-ui/Text/Text'
import { MessageType } from 'antd/lib/message'
import { WordPressClient } from 'api/clients/WordPressClient'
import { showToastMessage, showIntegrationSuccessToast } from 'common/ToastMessage/ShowToastMessage'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { BLAZE_WORDPRESS_PLUGIN_HELP_CENTER_URL } from 'paths'
import { AlertTriangle } from 'blaze-ui/icons/20/AlertTriangle'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'

const schema = yup.object().shape({
  url: yup
    .string()
    .required()
    .url()
    .test('no-wp-admin', 'URL should not include /wp-admin/', (value) => !value.includes('wp-admin'))
    .test('no-wp-login', 'URL should not include /wp-login/', (value) => !value.includes('wp-login')),
  username: yup.string().required(),
  password: yup.string().required(),
})

interface Props {
  onBack: () => void
  onSubmit?: (integration?: ExternalIntegration) => void
  switchFromIntegrationId?: number
}

export const WordPressForm = ({ onBack, onSubmit, switchFromIntegrationId }: Props) => {
  const workspaceId = useCurrentWorkspaceId()
  const loadingMessageRef = useRef<MessageType | undefined>()
  const { closeModal } = useModals()
  const queryClient = useQueryClient()
  const { isDesktop } = useResponsiveDesign()

  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const createIntegration = useMutation(WordPressClient.createIntegration, {
    onSuccess: async (response) => {
      loadingMessageRef.current?.()
      showIntegrationSuccessToast('Successfully connected to WordPress!')

      // Get the integration directly from the response
      if (response.type === SUCCESS_RESPONSE_TYPE && response.data?.external_integration) {
        const newIntegration = response.data.external_integration

        // Invalidate queries to refresh the list
        await queryClient.invalidateQueries(['externalIntegrations', workspaceId])

        closeModal()
        onSubmit?.(newIntegration)
      } else {
        closeModal()
        onSubmit?.()
      }
    },
    onMutate: () => {
      loadingMessageRef.current = showToastMessage('Connecting with WordPress...', 'loading')
    },
    onError: (e: Error) => {
      loadingMessageRef.current?.()
      showToastMessage('Unable to connect to WordPress', 'error')
      setErrorMessage(e.message)
    },
  })

  const handleSubmit = async (values: yup.InferType<typeof schema>) => {
    createIntegration.mutate({
      workspaceId,
      params: {
        ...values,
        ...(switchFromIntegrationId && { switch_from_integration_id: switchFromIntegrationId }),
      },
    })
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      validationSchema={schema}
      initialValues={{
        url: '',
        username: '',
        password: '',
      }}
    >
      {({ values, touched, errors, submitForm, setFieldValue }) => (
        <>
          <Modal.Content>
            {errorMessage && (
              <div className={Styles.errorContainer}>
                <span className={Styles.errorMessage}>{errorMessage}</span>
                <span>
                  Check out our <a href={BLAZE_WORDPRESS_PLUGIN_HELP_CENTER_URL}>Help Center</a> for common issues and
                  solutions.
                </span>
              </div>
            )}
            <div className={Styles.form}>
              <div className={Styles.fieldWithDescription}>
                <Field label="Base URL" isRequired>
                  <Input
                    size="lg"
                    name="url"
                    value={values.url}
                    errorMessage={touched.url && errors.url}
                    placeholder="https://my-wordpress-site.com"
                    onChange={(e) => setFieldValue('url', e.target.value)}
                  />
                </Field>
                <Text variant="p1" color="var(--neutral-50)">
                  Enter your full publicly accessible WordPress URL with leading http:// or https://. Do{' '}
                  <span className={Styles.bold}>not</span> include <kbd>/wp-admin/</kbd> or <kbd>/wp-login/</kbd>
                  <br />
                  <br />
                  If you have a <span className={Styles.bold}>custom login URL</span> please do not include the custom
                  URL value. For example: use <kbd>https://example.com</kbd> instead of{' '}
                  <kbd>https://example.com/login</kbd> as your Base URL
                </Text>
              </div>
              <Field label="Username" isRequired>
                <Input
                  size="lg"
                  name="username"
                  value={values.username}
                  errorMessage={touched.username && errors.username}
                  placeholder="Username you use to login to WordPress"
                  onChange={(e) => setFieldValue('username', e.target.value)}
                />
              </Field>
              <Field label="Application Password" isRequired>
                <Input
                  size="lg"
                  type="password"
                  name="password"
                  value={values.password}
                  errorMessage={touched.password && errors.password}
                  placeholder="Application password you created for Blaze"
                  onChange={(e) => setFieldValue('password', e.target.value)}
                />
              </Field>
              <div className={Styles.warningBanner}>
                <div className={Styles.warningIconContainer}>
                  <AlertTriangle color="white" size={24} />
                </div>
                <div className={Styles.warningContent}>
                  If you are using WordPress 5.6 or higher, you should use an{' '}
                  <a href="https://make.wordpress.org/core/2020/11/05/application-passwords-integration-guide/">
                    Application Password
                  </a>{' '}
                  for your password, otherwise you may experience issues authenticating.
                </div>
              </div>
            </div>
          </Modal.Content>
          <div className={Styles.footerContainer}>
            <Modal.Footer>
              <Modal.FooterContent slot="left">
                {isDesktop && (
                  <Button variant="tertiary" onPress={onBack}>
                    Back
                  </Button>
                )}
              </Modal.FooterContent>
              <Modal.FooterContent slot="right">
                <Button variant="primary" onPress={submitForm} size={isDesktop ? 'md' : 'lg'}>
                  Connect Account
                </Button>
              </Modal.FooterContent>
            </Modal.Footer>
          </div>
        </>
      )}
    </Formik>
  )
}
